/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — the «UI nuova» frame of the public shop (M18
 *              ec-shell): top band (logo, shop name, sections, languages,
 *              Area personale, cart), phone bottom tab bar, main column around
 *              the route outlet (yield), footer badge, the menu and cart
 *              BbPanels and the «not available online» contact dialog.
 *              Forked in templates/application.hbs with `(ui-is-nuova)`.
 *   extends:   Component (@glimmer/component)
 *   template:  ec-shell-nuova.hbs
 *   args:      @c (the application controller: every getter, action and task
 *              is the classic one — showMenu, showCart, totCart, cart,
 *              delCart, gotoMenu, openCartFromMenu, switchLangTask,
 *              noDispoOnline, sendContactMailTask …)
 *   uses:      service:intl (current locale when no `?lang=` is set), service:router
 *              (section: the current shop section for aria-current)
 *   siblings:  ec-shell-nuova/lang.hbs (language dropdown with flags, NvLangMenu),
 *              ec-shell-nuova/contact-mail.hbs (contact dialog),
 *              list-cart-product-nuova (cart lines)
 *   tests:     ui-nuova-ec-shell-test.js
 *   gotchas:   presentation getters only (controllers/application.js is a
 *              frozen god file). The outlet keeps the classic `content-wrap
 *              lang-<lang>` and `main-panel` classes: WrapTranslation and the
 *              pages' responsive toggles (.main-panel .dateSelected) key on
 *              them.
 */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { get } from '@ember/object';

export default class EcShellNuovaComponent extends Component {
  @service intl;
  @service router;

  // the shop section shown (section bar + tab bar aria-current; LinkTo alone only adds the `active` class)
  get section() {
    const name = this.router.currentRouteName || '';
    return ['index', 'list', 'gift', 'user'].find((s) => name === s || name.startsWith(`${s}.`)) ?? null;
  }

  // Two-letter code of the language shown, for the current row of the language dropdown: the `?lang=` query param
  // once switchLangTask ran (lower case), else the locale the intl service resolved at boot.
  get langCode() {
    const lang = this.args.c?.lang || this.intl?.primaryLocale || '';
    return String(lang).slice(0, 2).toLowerCase();
  }

  // The tenant uploaded a logo (the controller's thumbnail_url falls back to the Beebeeboard logo, which nuova
  // replaces with the shop's initial). `get`, not a dot-read: the application route fills the POJO afterwards with
  // set(controller, 'content.open_organization', …), and only get() consumes that key's tag — a dot-read stayed
  // false forever and the header showed the initial instead of the tenant logo.
  get hasLogo() {
    return Boolean(get(this.args, 'c.content.open_organization.thumbnail_url'));
  }
}
